import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import style from './Card.module.scss'
import cn from 'classnames'
import { Link } from '@kogk/gatsby-theme-base'
import { formatPriceString } from '@src/utils'
import productFallbackImg from '@src/assets/logos/LH-logo-trans.png'
import { FavoriteBtn } from '@cmp/site'
import CartIcon from '@root/src/assets/icons/CartIcon'
import CartIconFilled from '@root/src/assets/icons/CartIconFilled'
import Loading from '../Loading/Loading'
import { useCartActions, useCartState } from '@root/src/context/Cart'
import { useCartVisibility } from '@root/src/context/CartVisibility'
import { GET_PRODUCT_COUNT } from '@root/src/data/backend/queries'

const Card = ({
  url,
  prodDetail,
  image,
  price,
  discount,
  discountPrice = price,
  prodId,
  products,
  className
}) => {
  const [combined, setCombined] = useState(false)
  const [minPrice, setMinPrice] = useState(null)
  const [maxPrice, setMaxPrice] = useState(null)
  const [minFullPrice, setMinFullPrice] = useState(null)
  const [maxFullPrice, setMaxFullPrice] = useState(null)
  const [inventoryCount, setInventoryCount] = useState(null)

  const [currentItemLoading, setCurrentItemLoading] = useState(null)
  const { open: openCart } = useCartVisibility()
  const { cart } = useCartState()
  const { addProductToCart, changeQuantity, loading, error } = useCartActions({
    onAddedToCart: openCart
  })

  const getInventory = useQuery(GET_PRODUCT_COUNT, {
    ssr: false,
    variables: {
      id: products?.[0]?.id || prodId
    }
  })
  const invCount = getInventory.data?.navproduct?.inventoryCount

  const cartLoading = loading && currentItemLoading == prodId
  const inCart = cart?.products?.some(
    product => product.productGroup?.id == prodId
  )

  useEffect(() => {
    if (products?.length > 1) {
      setCombined(true)

      const prices = products.map(item => item.discountPrice)
      const min = Math.min(...prices)
      const max = Math.max(...prices)

      if (min < max) {
        setMinPrice(min)
        setMaxPrice(max)
      }

      const fullPrices = products.map(item => item.fullPrice)
      const minFull = Math.min(...fullPrices)
      const maxFull = Math.max(...fullPrices)

      if (minFull < maxFull) {
        setMinFullPrice(minFull)
        setMaxFullPrice(maxFull)
      }
    }
  }, [products])

  return (
    <div className={cn(className, style.card)}>
      <FavoriteBtn id={prodId} className={style.favoriteBtn} />

      <Link to={url} className={style.cardLink}>
        {discount > 0 && (
          <span className={style.discountLabel}>-{discount}%</span>
        )}

        <div className={style.imageWrapper}>
          {image ? (
            <img
              src={image}
              alt={prodDetail}
              loading='lazy'
              className={style.image}
            />
          ) : (
            <div className={style.placeholder}>
              <div style={{ backgroundImage: `url(${productFallbackImg})` }} />
            </div>
          )}
        </div>
        <div className={cn(style.prodDetail, 'prod-title pt-1 opacity--08')}>
          {prodDetail}
        </div>
        {combined && <div className='prod-title'>{products.length} vörur</div>}
        <div className='d-flex justify-content-between'>
          <div className='d-flex flex-column'>
            {discount > 0 && (
              <p className={cn(style.price, 'price-tag price-tag--striked')}>
                {combined && minFullPrice && maxFullPrice
                  ? `${formatPriceString(
                      minFullPrice,
                      false
                    )} - ${formatPriceString(maxFullPrice, true)}`
                  : formatPriceString(price, true)}
              </p>
            )}
            <p
              className={cn(
                style.priceTag,
                'price-tag',
                { 'price-tag--discount': discount > 0 },
                {
                  'price-tag--combined':
                    combined && minFullPrice && maxFullPrice
                }
              )}
            >
              {combined && minPrice && maxPrice
                ? `${formatPriceString(minPrice, false)} - ${formatPriceString(
                    maxPrice,
                    true
                  )}`
                : formatPriceString(discountPrice, true)}
            </p>
          </div>
          {!combined && (!getInventory?.data?.navproduct || invCount > 0) ? (
            inCart ? (
              <div
                className={style.cart}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setCurrentItemLoading(prodId)
                  changeQuantity(products?.[0]?.id, 0)
                }}
              >
                {cartLoading ? <Loading size='md' /> : <CartIconFilled />}
              </div>
            ) : (
              <div
                className={style.cart}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setCurrentItemLoading(prodId)
                  addProductToCart({
                    productGroupId: prodId,
                    productId: products?.[0]?.id,
                    quantity: 1
                  })
                }}
              >
                {cartLoading ? <Loading size='md' /> : <CartIcon />}
              </div>
            )
          ) : combined ? null : (
            <div className={style.cart}>
              <p className={style.outOfStock}>Uppselt</p>
            </div>
          )}
        </div>
      </Link>
    </div>
  )
}

export default Card
